import { apiFetch } from "../utils/apiUtils";
import { Subscription } from "../types/auth";

export interface SubscriptionStatusResponse {
  subscription?: Subscription | null;
}

export interface SubscriptionSyncResult {
  ok: boolean;
  subscription: Subscription | null;
}

/** The plans personal Pro is sold as (PRD 87 §3). Checkout takes exactly these. */
export type ProPlan = "monthly" | "annual";

/**
 * One line of GET /api/v1/subscription/plans, priced from the book checkout
 * would charge.
 *
 * `amount_cents` is null when the book has no price for the line — unknown,
 * not free — and such a plan is never sellable (nil is not zero).
 */
export interface SubscriptionPlan {
  plan: string;
  amount_cents: number | null;
  currency: string;
  interval: string;
  sellable: boolean;
}

/**
 * What checkout would refuse with right now, decided from the api's own
 * records. Checkout stays the authoritative check, and answers with the same
 * codes when it refuses.
 */
export interface SubscriptionRefusal {
  code: string;
  message: string;
  details: Record<string, unknown>;
}

export interface SubscriptionPlans {
  plans: SubscriptionPlan[];
  /** When checkout would first charge, if the person already has Pro until then. */
  first_charge_deferred_until: string | null;
  refusal: SubscriptionRefusal | null;
}

export interface CheckoutSession {
  url: string;
  session_id: string;
  plan: string;
  first_charge_deferred_until: string | null;
}

export interface StartCheckoutOptions {
  /**
   * The app the person came from, which Stripe hands back on the return URL.
   * A scheme only: the hand-off's return_url carries the person's tokens and
   * never leaves the web (PRD 113 §5), so this method cannot send one.
   */
  returnScheme?: string;
}

export interface SubscriptionFieldError {
  field: string;
  message: string;
}

/**
 * 409s carry an object (`subscription_status`, `covered_until`);
 * validation_failed carries one entry per field; everything else an empty list.
 */
export type SubscriptionErrorDetails = Record<string, unknown> | SubscriptionFieldError[];

/** No response at all: offline, DNS, CORS, a dropped connection. */
export const NETWORK_ERROR_CODE = "network_error";
/** A 2xx whose body is not the documented shape. Never treated as success. */
export const INVALID_RESPONSE_CODE = "invalid_response";
/** The message of a refusal whose body said none: a stand-in, not a sentence to show. */
export const requestFailedMessage = (status: number): string => `Request failed (${status})`;

/**
 * A refused or failed subscription request, from the api's
 * `{ error: { code, message, details } }` envelope (PRD 00 §8.3).
 * `status` is 0 when no response arrived.
 */
export class SubscriptionApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: SubscriptionErrorDetails;

  constructor(status: number, code: string, message: string, details: SubscriptionErrorDetails = []) {
    super(message);
    this.name = "SubscriptionApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** A string field of an object `details`, or null. */
  detail(key: string): string | null {
    if (Array.isArray(this.details)) return null;
    const value = this.details[key];
    return typeof value === "string" ? value : null;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const send = async (path: string, init: RequestInit): Promise<Response> => {
  try {
    return await apiFetch(path, init);
  } catch {
    throw new SubscriptionApiError(0, NETWORK_ERROR_CODE, "Could not reach Greenback.");
  }
};

const toDetails = (raw: unknown): SubscriptionErrorDetails => {
  if (Array.isArray(raw)) {
    return raw
      .filter(isRecord)
      .map((entry) => ({ field: String(entry.field ?? ""), message: String(entry.message ?? "") }));
  }
  return isRecord(raw) ? raw : [];
};

/**
 * The `data` of a 2xx, or a thrown SubscriptionApiError. apiFetch never throws
 * on an HTTP status, so this is the only thing standing between a refusal and
 * a caller that thinks it succeeded.
 */
const parseData = async (response: Response): Promise<Record<string, unknown>> => {
  const body = await readJson(response);

  if (!response.ok) {
    const error = isRecord(body) ? body.error : undefined;
    if (isRecord(error)) {
      throw new SubscriptionApiError(
        response.status,
        typeof error.code === "string" && error.code ? error.code : "http_error",
        typeof error.message === "string" && error.message ? error.message : requestFailedMessage(response.status),
        toDetails(error.details)
      );
    }
    throw new SubscriptionApiError(
      response.status,
      "http_error",
      typeof error === "string" && error ? error : requestFailedMessage(response.status)
    );
  }

  const data = isRecord(body) ? body.data : undefined;
  if (!isRecord(data)) {
    throw new SubscriptionApiError(response.status, INVALID_RESPONSE_CODE, "Unexpected response from Greenback.");
  }
  return data;
};

const optionalString = (value: unknown): string | null =>
  typeof value === "string" && value ? value : null;

/** Where Checkout or the portal sends the browser: only ever a web URL. */
const webUrl = (value: unknown, status: number): string => {
  if (typeof value === "string") {
    try {
      const { protocol } = new URL(value);
      if (protocol === "https:" || protocol === "http:") return value;
    } catch {
      // fall through
    }
  }
  throw new SubscriptionApiError(status, INVALID_RESPONSE_CODE, "Unexpected response from Greenback.");
};

const toPlan = (raw: unknown): SubscriptionPlan | null => {
  if (!isRecord(raw) || typeof raw.plan !== "string" || !raw.plan) return null;
  const cents = raw.amount_cents;
  const amount = typeof cents === "number" && Number.isInteger(cents) && cents >= 0 ? cents : null;
  return {
    plan: raw.plan,
    amount_cents: amount,
    currency: typeof raw.currency === "string" && raw.currency ? raw.currency : "usd",
    interval: typeof raw.interval === "string" ? raw.interval : "",
    // An unpriced line cannot be bought whatever the flag says.
    sellable: raw.sellable === true && amount !== null,
  };
};

const toRefusal = (raw: unknown): SubscriptionRefusal | null => {
  if (!isRecord(raw) || typeof raw.code !== "string" || !raw.code) return null;
  return {
    code: raw.code,
    message: typeof raw.message === "string" ? raw.message : "",
    details: isRecord(raw.details) ? raw.details : {},
  };
};

class SubscriptionService {
  private static instance: SubscriptionService;

  public static getInstance(): SubscriptionService {
    if (!SubscriptionService.instance) {
      SubscriptionService.instance = new SubscriptionService();
    }
    return SubscriptionService.instance;
  }

  async getSubscription(): Promise<Subscription | null> {
    try {
      const response = await apiFetch("/api/v1/subscription", { method: "GET" });
      if (!response.ok) return null;
      const data: SubscriptionStatusResponse = await response.json();
      return data.subscription ?? null;
    } catch {
      return null;
    }
  }

  async syncSubscriptionStatus(): Promise<SubscriptionSyncResult> {
    try {
      const response = await apiFetch("/api/v1/subscription/sync", { method: "POST" });
      if (!response.ok) {
        return { ok: false, subscription: null };
      }

      const data: SubscriptionStatusResponse = await response.json();
      return { ok: true, subscription: data.subscription ?? null };
    } catch {
      return { ok: false, subscription: null };
    }
  }

  async cancelSubscription(): Promise<{ ok: boolean; error?: string; subscription?: Subscription | null }> {
    try {
      const response = await apiFetch("/api/v1/subscription/cancel", { method: "POST" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { ok: false, error: data.error || "Cancellation failed. Please try again." };
      }
      return { ok: true, subscription: data.subscription ?? null };
    } catch {
      return { ok: false, error: "Network error. Please try again." };
    }
  }

  async syncSubscription(): Promise<Subscription | null> {
    const result = await this.syncSubscriptionStatus();
    return result.subscription;
  }

  /**
   * GET /api/v1/subscription/plans: what /subscribe offers, priced from the
   * book checkout would charge, and what checkout would refuse right now.
   * Throws SubscriptionApiError.
   */
  async getPlans(): Promise<SubscriptionPlans> {
    const response = await send("/api/v1/subscription/plans", { method: "GET" });
    const data = await parseData(response);
    if (!Array.isArray(data.plans)) {
      throw new SubscriptionApiError(response.status, INVALID_RESPONSE_CODE, "Unexpected response from Greenback.");
    }
    return {
      plans: data.plans.map(toPlan).filter((plan): plan is SubscriptionPlan => plan !== null),
      first_charge_deferred_until: optionalString(data.first_charge_deferred_until),
      refusal: toRefusal(data.refusal),
    };
  }

  /**
   * POST /api/v1/subscription/checkout: a Stripe Checkout session to send the
   * person to. Throws SubscriptionApiError, including for every refusal.
   */
  async startCheckout(plan: ProPlan, options: StartCheckoutOptions = {}): Promise<CheckoutSession> {
    const body: { plan: ProPlan; return_scheme?: string } = { plan };
    if (options.returnScheme) body.return_scheme = options.returnScheme;

    const response = await send("/api/v1/subscription/checkout", {
      method: "POST",
      body: JSON.stringify(body),
    });
    const data = await parseData(response);
    return {
      url: webUrl(data.url, response.status),
      session_id: typeof data.session_id === "string" ? data.session_id : "",
      plan: typeof data.plan === "string" ? data.plan : plan,
      first_charge_deferred_until: optionalString(data.first_charge_deferred_until),
    };
  }

  /**
   * POST /api/v1/subscription/portal: a Stripe billing-portal session for
   * cards, receipts and cancellation. 422 no_billing_account when the person
   * has no Stripe customer. Throws SubscriptionApiError.
   */
  async openPortal(): Promise<{ url: string }> {
    const response = await send("/api/v1/subscription/portal", { method: "POST" });
    const data = await parseData(response);
    return { url: webUrl(data.url, response.status) };
  }
}

export const subscriptionService = SubscriptionService.getInstance();

// The same envelope, refusals and URL check, for billingAccountsService
// (PRD 114): one parser for one wire format.
export { send as sendBillingRequest, parseData as parseBillingData, webUrl as requireWebUrl };
